import {
  parseAsIndex,
  parseAsInteger,
  parseAsJson,
  parseAsNativeArrayOf,
  parseAsString,
  useQueryState,
  useQueryStates,
} from "nuqs"
import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from "@tanstack/react-table"

type StateUpdater<TValue> = TValue | ((previous: TValue) => TValue)

const parseColumnFiltersState = parseAsJson<ColumnFiltersState>(
  (value): ColumnFiltersState | null => Array.isArray(value) ? value as ColumnFiltersState : null
)

const signedSortFieldPattern = /^[A-Za-z0-9_.]+$/

function isAllowedValue(value: string, allowedValues?: readonly string[]): boolean {
  return !allowedValues || allowedValues.includes(value)
}

function isSortingState(value: unknown): value is SortingState {
  return Array.isArray(value) && value.every((item) => (
    typeof item === "object" &&
    item !== null &&
    typeof (item as { id?: unknown }).id === "string" &&
    typeof (item as { desc?: unknown }).desc === "boolean"
  ))
}

/** Convert canonical repeated signed sort tokens into TanStack sorting state. */
export function parseSignedSortingState(
  tokens: readonly string[],
  allowedFields?: readonly string[]
): SortingState {
  if (tokens.length === 1 && tokens[0]?.trim().startsWith("[")) {
    try {
      const legacyValue: unknown = JSON.parse(tokens[0])
      if (isSortingState(legacyValue)) {
        return legacyValue.filter(({ id }) => (
          signedSortFieldPattern.test(id) && isAllowedValue(id, allowedFields)
        ))
      }
    } catch {
      return []
    }
  }

  const seenFields = new Set<string>()
  const sorting: SortingState = []
  for (const token of tokens) {
    const normalizedToken = token.trim()
    const desc = normalizedToken.startsWith("-")
    const id = desc ? normalizedToken.slice(1) : normalizedToken
    if (
      !signedSortFieldPattern.test(id) ||
      !isAllowedValue(id, allowedFields) ||
      seenFields.has(id)
    ) {
      continue
    }
    seenFields.add(id)
    sorting.push({ id, desc })
  }
  return sorting
}

/** Convert TanStack sorting state into ordered canonical signed sort tokens. */
export function serializeSignedSortingState(
  sorting: SortingState,
  allowedFields?: readonly string[]
): string[] {
  const seenFields = new Set<string>()
  return sorting.flatMap(({ id, desc }) => {
    if (
      !signedSortFieldPattern.test(id) ||
      !isAllowedValue(id, allowedFields) ||
      seenFields.has(id)
    ) {
      return []
    }
    seenFields.add(id)
    return [desc ? `-${id}` : id]
  })
}

/** Canonicalize repeated facet values without changing their meaning. */
export function normalizeDataTableFacetValues(values: readonly unknown[]): string[] {
  return [...new Set(values.map(String).map((value) => value.trim()).filter(Boolean))].sort()
}

export interface DataTableStateOptions {
  /** The prefix for URL parameters when multiple tables share one route. */
  prefix?: string
  /** Default pagination state. */
  defaultPagination?: PaginationState
  /** Default ordered sort state. */
  defaultSorting?: SortingState
  /** Endpoint-allow-listed sort fields. Invalid URL tokens are ignored. */
  sortFields?: readonly string[]
  /** Endpoint-allow-listed facet keys serialized as repeated query parameters. */
  facetKeys?: readonly string[]
  /** Optional active registry for routes that host multiple registries. */
  defaultRegistry?: string
  /** Allowed registry identifiers. Invalid URL values fall back to the default. */
  registryValues?: readonly string[]
  /** Whether to scroll to the table on URL state changes. */
  scrollToTop?: boolean
}

function createRepeatedStringParser() {
  return parseAsNativeArrayOf(parseAsString).withDefault([]).withOptions({
    history: "replace",
    clearOnDefault: true,
  })
}

/**
 * Manage canonical DataTable query state with nuqs while retaining the existing
 * TanStack-compatible return shape. Facet keys must be declared by the host so
 * arbitrary query parameters never become filters.
 */
export function useDataTableUrlState(options: DataTableStateOptions = {}) {
  const {
    prefix = "",
    defaultPagination = { pageIndex: 0, pageSize: 25 },
    defaultSorting = [],
    sortFields,
    facetKeys = [],
    defaultRegistry = "",
    registryValues,
    scrollToTop = true,
  } = options

  const getParamName = (name: string) => prefix ? `${prefix}_${name}` : name
  const normalizedFacetKeySignature = [...new Set(facetKeys)].sort().join("\u0000")
  const normalizedFacetKeys = normalizedFacetKeySignature
    ? normalizedFacetKeySignature.split("\u0000")
    : []

  const [pageIndex, setPageIndex] = useQueryState(
    getParamName("page"),
    parseAsIndex.withDefault(defaultPagination.pageIndex).withOptions({
      history: "replace",
      scroll: scrollToTop,
      clearOnDefault: true,
    })
  )
  const [canonicalPageSize, setCanonicalPageSize] = useQueryState(
    getParamName("page_size"),
    parseAsInteger.withOptions({ history: "replace", scroll: scrollToTop })
  )
  // Read the former `size` key so bookmarked table URLs remain usable. Every
  // subsequent size change writes only the canonical `page_size` key.
  const [legacyPageSize, setLegacyPageSize] = useQueryState(
    getParamName("size"),
    parseAsInteger.withOptions({ history: "replace", scroll: scrollToTop })
  )
  const pageSize = canonicalPageSize && canonicalPageSize > 0
    ? canonicalPageSize
    : legacyPageSize && legacyPageSize > 0
      ? legacyPageSize
      : defaultPagination.pageSize
  const pagination = { pageIndex, pageSize }

  const defaultSortTokens = serializeSignedSortingState(defaultSorting, sortFields)
  const [sortTokens, setSortTokens] = useQueryState(
    getParamName("sort"),
    parseAsNativeArrayOf(parseAsString).withDefault(defaultSortTokens).withOptions({
      history: "replace",
      clearOnDefault: true,
    })
  )
  const sorting = parseSignedSortingState(sortTokens, sortFields)

  const facetParsers = Object.fromEntries(
    normalizedFacetKeys.map((facetKey) => [facetKey, createRepeatedStringParser()])
  ) as Record<string, ReturnType<typeof createRepeatedStringParser>>
  const [rawFacetValues, setRawFacetValues] = useQueryStates(facetParsers)
  const facetValues = Object.fromEntries(
    normalizedFacetKeys.map((facetKey) => [
      facetKey,
      normalizeDataTableFacetValues(rawFacetValues[facetKey] ?? []),
    ])
  )

  // Preserve legacy JSON `filter` links and non-facet column filters. Declared
  // facets always use their canonical repeated keys when state is next changed.
  const [legacyColumnFilters, setLegacyColumnFilters] = useQueryState<ColumnFiltersState>(
    getParamName("filter"),
    parseColumnFiltersState.withDefault([]).withOptions({ history: "replace" })
  )
  const columnFilters: ColumnFiltersState = [
    ...legacyColumnFilters.filter((filter) => !normalizedFacetKeys.includes(filter.id)),
    ...normalizedFacetKeys.flatMap((facetKey) => {
      const values = facetValues[facetKey] ?? []
      return values.length > 0 ? [{ id: facetKey, value: values }] : []
    }),
  ]

  const [globalFilter, setGlobalFilter] = useQueryState(
    getParamName("q"),
    parseAsString.withDefault("").withOptions({
      history: "replace",
      clearOnDefault: true,
    })
  )
  const [rawRegistry, setRawRegistry] = useQueryState(
    getParamName("registry"),
    parseAsString.withDefault(defaultRegistry).withOptions({
      history: "replace",
      clearOnDefault: true,
    })
  )
  const registry = isAllowedValue(rawRegistry, registryValues) ? rawRegistry : defaultRegistry

  const onPaginationChange = (updaterOrValue: StateUpdater<PaginationState>) => {
      const next = typeof updaterOrValue === "function"
        ? updaterOrValue(pagination)
        : updaterOrValue
      const sizeChanged = next.pageSize !== pagination.pageSize
      void setPageIndex(sizeChanged ? 0 : next.pageIndex)
      if (sizeChanged) {
        void setCanonicalPageSize(
          next.pageSize === defaultPagination.pageSize ? null : next.pageSize
        )
        void setLegacyPageSize(null)
      }
    }

  const onSortingChange = (updaterOrValue: StateUpdater<SortingState>) => {
      const next = typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue
      void setSortTokens(serializeSignedSortingState(next, sortFields))
      void setPageIndex(0)
    }

  const onColumnFiltersChange = (updaterOrValue: StateUpdater<ColumnFiltersState>) => {
      const next = typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue
      const nextFacetValues = Object.fromEntries(normalizedFacetKeys.map((facetKey) => {
        const filter = next.find((candidate) => candidate.id === facetKey)
        const values = Array.isArray(filter?.value) ? filter.value : filter?.value ? [filter.value] : []
        return [facetKey, normalizeDataTableFacetValues(values)]
      }))
      const nextLegacyFilters = next.filter((filter) => !normalizedFacetKeys.includes(filter.id))
      void setRawFacetValues(nextFacetValues)
      void setLegacyColumnFilters(nextLegacyFilters.length > 0 ? nextLegacyFilters : null)
      void setPageIndex(0)
    }

  const onFacetValuesChange = (facetKey: string, values: readonly string[]) => {
    if (!normalizedFacetKeys.includes(facetKey)) {
      return
    }
    void setRawFacetValues({ [facetKey]: normalizeDataTableFacetValues(values) })
    void setPageIndex(0)
  }

  const onGlobalFilterChange = (updaterOrValue: StateUpdater<string | undefined>) => {
    const previous = globalFilter || undefined
    const next = typeof updaterOrValue === "function"
      ? updaterOrValue(previous)
      : updaterOrValue
    void setGlobalFilter(next?.trim() || null)
    void setPageIndex(0)
  }

  const onRegistryChange = (value: string | undefined) => {
    const next = value?.trim() || defaultRegistry
    if (!isAllowedValue(next, registryValues)) {
      return
    }
    void setRawRegistry(next === defaultRegistry ? null : next)
    void setPageIndex(0)
  }

  return {
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    columnFilters,
    onColumnFiltersChange,
    facetValues,
    onFacetValuesChange,
    globalFilter: globalFilter || undefined,
    onGlobalFilterChange,
    registry: registry || undefined,
    onRegistryChange,
  }
}
